/**
 * Start a yard on a lawn plan, from the customer's card, right now.
 *
 * For a customer who has already said yes (owner, Sep 19 2026: "he said yes Thursday, get him started"). A bid to
 * sign is the other door — that one is PlanBidButton, for people still deciding.
 *
 * Two kinds of plan:
 *   Full / The Plan / Simple   the three on the cost sheet, priced for the yard
 *   Annual program             the 4 fertilizer / 2 pre-emergent / 2 post-emergent year he sold before the app
 *
 * Either can be started at a price he already quoted, which is the point: the sheet's price for a lawn is a
 * starting point, and what he shook hands on wins. It looks first, then books.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { api } from '../services/api';
import CustomPlanBuilder from './CustomPlanBuilder';

type Yard = { property_id: number; address?: string | null; lot_sqft?: number | null; turf_sqft?: number | null; turf_known?: boolean; grass?: string | null;
  old_treatment?: { schedule_id: number; price: number | null; future_visits: number } | null };
const PLANS = [
  { key: 'full', name: 'Estate', hint: 'the complete year' },
  { key: 'plan', name: 'The Plan', hint: 'most yards' },
  { key: 'simple', name: 'Simple', hint: 'feed and prevent' },
  { key: 'annual', name: 'Annual program', hint: '4 fertilizer, 2 pre, 2 weed control' },
];
const GRASS_KEYS: Record<string, string> = { 'St. Augustine': 'sa', 'Bermuda / Zoysia': 'bz' };

export default function StartPlanForm({ yard, customerId }: { yard: Yard; customerId: number }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState('plan');
  const [monthly, setMonthly] = useState('');
  const [sqft, setSqft] = useState(yard.turf_known && yard.turf_sqft ? String(yard.turf_sqft) : '');
  const [grass, setGrass] = useState('');
  // His count of what went down this year before the app (Sep 29 2026: "all the ferts and pres have not been done, only spray").
  const [done, setDone] = useState({ pre: '0', fertilizer: '0', post: '1' });
  const [look, setLook] = useState<any>(null);
  // Their own mix — a plan plus extras in chosen months (owner, Oct 22 2026: Scott's Simple + core, chinch bug and grub in August).
  const [custom, setCustom] = useState(false);

  const annual = plan === 'annual';
  const needGrass = !yard.grass || !GRASS_KEYS[yard.grass];
  const halfLot = yard.lot_sqft ? Math.max(500, Math.round((Number(yard.lot_sqft) / 2) / 500) * 500) : null;
  const clear = () => setLook(null);

  const body = (dry: boolean) => (annual
    ? {
        property_id: yard.property_id, monthly_price: Number(monthly) || undefined,
        sqft: Number(sqft) > 0 ? Number(sqft) : undefined, grass: grass || undefined,
        done: { pre: Number(done.pre) || 0, fertilizer: Number(done.fertilizer) || 0, post: Number(done.post) || 0 },
        dry_run: dry,
      }
    : {
        property_id: yard.property_id, plan,
        monthly_price: Number(monthly) > 0 ? Number(monthly) : undefined,
        sqft: Number(sqft) > 0 ? Number(sqft) : undefined, grass: grass || undefined,
        dry_run: dry,
      });
  const url = () => (annual ? '/treatments/annual-program' : '/plan-sheet/enroll');

  const preview = useMutation(() => api.post(url(), body(true)).then(r => r.data), {
    onSuccess: (d: any) => setLook(annual ? { annual: d.plan } : { sheet: d.preview }),
    onError: (e: any) => { setLook(null); toast.error(e?.response?.data?.message || 'Could not work that out'); },
  });
  const start = useMutation(() => api.post(url(), body(false)).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'On the plan', { autoClose: 8000 });
      setOpen(false); setLook(null);
      qc.invalidateQueries(['customer-plan-options', customerId]);
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not start that plan'); },
  });

  // Still on the old every-4-weeks lawn treatment: a plan replaces it, so starting one stops it (it used to run on too —
  // two visits and two charges, audit Oct 1 2026).
  const oldJob = yard.old_treatment
    ? <p className="mt-1 text-xs text-amber-800">On the old every-4-weeks lawn treatment{yard.old_treatment.price ? ` at $${yard.old_treatment.price} a visit` : ''}. Starting a plan stops it{yard.old_treatment.future_visits ? ` and takes its ${yard.old_treatment.future_visits} booked visit${yard.old_treatment.future_visits === 1 ? '' : 's'} off` : ''}.</p>
    : null;

  if (custom) {
    return <>{oldJob}<CustomPlanBuilder propertyId={yard.property_id} customerId={customerId} onDone={() => setCustom(false)} onCancel={() => setCustom(false)} /></>;
  }
  if (!open) {
    return <>{oldJob}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        <button type="button" onClick={() => setOpen(true)} className="text-xs font-semibold text-emerald-800 underline underline-offset-2">Start them on a plan now (they already said yes)</button>
        <button type="button" onClick={() => setCustom(true)} className="text-xs font-semibold text-emerald-800 underline underline-offset-2">Build a custom plan (extras, months)</button>
      </div></>;
  }

  const sheet = look?.sheet, ann = look?.annual;
  return (
    <div className="mt-2 rounded-lg border border-emerald-300 bg-emerald-50/60 p-2.5 space-y-2">
      <p className="text-xs font-bold text-emerald-900">Start a lawn plan</p>
      {oldJob}

      <div className="flex flex-wrap gap-1.5">
        {PLANS.map(p => (
          <button key={p.key} type="button" onClick={() => { setPlan(p.key); clear(); }}
            className={`text-xs px-2.5 py-1.5 rounded-lg border font-semibold ${plan === p.key ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-gray-700 border-gray-300'}`}
            title={p.hint}>{p.name}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="text-[11px] text-gray-600">What they pay a month ($)
          <input type="number" inputMode="decimal" className="input mt-0.5 py-1.5" value={monthly} onChange={e => { setMonthly(e.target.value); clear(); }}
            placeholder={annual ? 'required' : 'blank = the sheet price'} />
          {!annual && <span className="block text-[11px] text-gray-500 mt-0.5">Type it when you have already quoted a price. It wins over the sheet.</span>}</label>
        <label className="text-[11px] text-gray-600">Lawn they pay for (sq ft)
          <input type="number" inputMode="numeric" className="input mt-0.5 py-1.5" value={sqft} onChange={e => { setSqft(e.target.value); clear(); }}
            placeholder={halfLot ? `half the lot is ${halfLot.toLocaleString()}` : 'sq ft of grass'} />
          {halfLot && String(halfLot) !== sqft && <button type="button" onClick={() => { setSqft(String(halfLot)); clear(); }} className="mt-0.5 text-[11px] text-emerald-800 underline">Use half the lot — {halfLot.toLocaleString()}</button>}</label>
        {needGrass && (
          <label className="text-[11px] text-gray-600 col-span-2">Grass <span className="text-gray-500">(the weed spray is different for each)</span>
            <select className="input mt-0.5 py-1.5" value={grass} onChange={e => { setGrass(e.target.value); clear(); }}>
              <option value="">Pick one…</option><option value="sa">St. Augustine</option><option value="bz">Bermuda / Zoysia</option>
            </select></label>
        )}
      </div>

      {annual && (
        <div>
          <p className="text-[11px] text-gray-600">Already done this year, before the app:</p>
          <div className="grid grid-cols-3 gap-2 mt-0.5">
            {([['pre', 'Pre-emergent', 2], ['fertilizer', 'Fertilizer', 4], ['post', 'Weed control', 2]] as const).map(([k, label, max]) => (
              <label key={k} className="text-[11px] text-gray-600">{label} <span className="text-gray-500">of {max}</span>
                <input type="number" inputMode="numeric" min={0} max={max} className="input mt-0.5 py-1.5" value={(done as any)[k]} onChange={e => { setDone(d => ({ ...d, [k]: e.target.value })); clear(); }} /></label>
            ))}
          </div>
        </div>
      )}

      {sheet && (
        <div className="rounded-lg bg-white border border-emerald-200 p-2 text-xs text-gray-800 space-y-1">
          <p><b>{sheet.plan_name}</b> at <b>${sheet.monthly}/mo</b>{sheet.card && sheet.card.month !== sheet.monthly ? <span className="text-gray-500"> (the sheet says ${sheet.card.month})</span> : null} · {sheet.grass_name}{Number(sheet.sqft) > 0 ? <> · {Number(sheet.sqft).toLocaleString()} sq ft</> : null} · starts {sheet.start}</p>
          <p className="text-gray-600">{sheet.visits?.length || 0} visits booked, {(sheet.visits || []).filter((v: any) => v.rides).length} of them riding a mow. First: {sheet.visits?.[0] ? `${sheet.visits[0].date} ${sheet.visits[0].words}` : '—'}</p>
          {sheet.old_schedule_words && <p className="text-amber-800">{sheet.old_schedule_words}</p>}
        </div>
      )}
      {ann && (
        <div className="rounded-lg bg-white border border-emerald-200 p-2 text-xs text-gray-800 space-y-1">
          <p><b>${ann.monthly}/mo</b>, billed monthly from {ann.billed_monthly_from} · {ann.grass}{Number(ann.sqft) > 0 ? <> · {Number(ann.sqft).toLocaleString()} sq ft</> : null}</p>
          <p className="text-gray-600">Still to do this year: {ann.this_year.length ? ann.this_year.map((v: any) => `${v.date} ${v.words}`).join(' · ') : 'nothing'}</p>
          <p className="text-gray-600">{ann.next_year_visits} visits booked for next year.</p>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!look
          ? <button type="button" onClick={() => preview.mutate()} disabled={preview.isLoading || (annual && !(Number(monthly) > 0))}
              className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{preview.isLoading ? 'Looking…' : 'Look first'}</button>
          : <button type="button" onClick={() => start.mutate()} disabled={start.isLoading}
              className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-700 text-white font-semibold disabled:opacity-50">{start.isLoading ? 'Starting…' : 'Start them on it'}</button>}
        <button type="button" onClick={() => { setOpen(false); clear(); }} className="text-xs text-gray-500">Cancel</button>
        <span className="text-[11px] text-gray-500">Nothing is texted or charged. Each treatment rides their mow as one stop.</span>
      </div>
    </div>
  );
}
